import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "../constants/site";
import { useScrollTo } from "../lib/scroll";
import Counter from "./Counter";
import SpinBadge from "./SpinBadge";

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 1.95 + i * 0.16, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const ref = useRef(null);
  const scrollTo = useScrollTo();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Sugee Lake District tower" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#FBFAF7] via-transparent to-black/40" />

      <motion.div style={{ opacity: contentOpacity }} className="relative z-10 max-w-screen-2xl mx-auto px-6 min-h-screen flex flex-col justify-end pb-32 sm:pb-24 pt-32">
        <motion.p {...fade(1.75)} className="text-[11px] uppercase tracking-[0.45em] text-[#9CC3EE] mb-6 flex items-center gap-4">
          <span className="inline-block w-12 h-px bg-[#9CC3EE]/70" />
          <span className="w-2 h-2 rounded-full bg-[#25D366] animate-pulse" />
          New Launch · 35 Office Floors · Thane West
        </motion.p>

        <h1 className="font-display font-light leading-[0.95] tracking-tight text-6xl sm:text-7xl lg:text-8xl xl:text-9xl">
          {["SUGEE", "LAKE DISTRICT"].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
                className={`block ${i === 1 ? "italic shimmer-text" : "text-white"}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
          <motion.div {...fade(2.55)} className="max-w-xl">
            <p className="text-white/70 text-sm md:text-base leading-relaxed">
              Grade A+ waterfront workspaces opposite Korum Mall, near Cadbury Junction.
              Ultra-luxury office suites from <span className="text-white">₹2.64 Cr*</span> onwards.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={() => scrollTo("#enquire")}
                data-testid="hero-pre-register-button"
                className="bg-primary text-primary-foreground text-[11px] uppercase tracking-[0.25em] px-8 py-4 hover:bg-[#d8bc8f] transition-colors"
              >
                Pre-Register Now
              </button>
              <button
                onClick={() => scrollTo("#vision")}
                data-testid="hero-explore-button"
                className="border border-white/30 text-white text-[11px] uppercase tracking-[0.25em] px-8 py-4 hover:border-[#9CC3EE] hover:text-[#9CC3EE] transition-colors"
              >
                Explore the Vision
              </button>
            </div>
            <div data-testid="hero-micro-stats" className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-[10px] uppercase tracking-[0.3em] text-white/50">
              <span className="flex items-baseline gap-2"><span className="font-display font-semibold text-2xl text-white normal-case tracking-normal"><Counter value={35} /></span>Office Floors</span>
              <span className="w-px h-4 bg-white/20" />
              <span className="flex items-baseline gap-2"><span className="font-display font-semibold text-2xl text-white normal-case tracking-normal"><Counter value={52} /></span>Acres</span>
              <span className="w-px h-4 bg-white/20" />
              <span className="flex items-baseline gap-2"><span className="font-display font-semibold text-2xl text-white normal-case tracking-normal"><Counter value={3} /></span>Min to Metro</span>
            </div>
          </motion.div>

          <motion.div
            {...fade(2.75)}
            data-testid="hero-price-chip"
            className="hidden sm:block backdrop-blur-xl bg-black/40 border border-white/10 px-8 py-6 self-start lg:self-end"
          >
            <p className="text-[10px] uppercase tracking-[0.35em] text-white/50 mb-2">Ultra-Luxury Offices</p>
            <p className="font-display text-4xl text-[#9CC3EE]">₹2.64 Cr*</p>
            <p className="text-xs text-white/60 mt-1">onwards · 800 – 46,000 sq.ft suites</p>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.4, duration: 1 }}
        className="absolute bottom-6 left-6 z-10 flex items-center gap-3"
      >
        <span className="text-[10px] uppercase tracking-[0.4em] text-white/60">Scroll</span>
        <motion.span
          className="block w-10 h-px bg-[#9CC3EE]/70 origin-left"
          animate={{ scaleX: [0, 1, 0] }}
          transition={{ repeat: Infinity, duration: 2.6, ease: "easeInOut" }}
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.0, duration: 0.8 }}
        className="absolute top-[22%] right-[6%] hidden lg:block z-10"
      >
        <div data-testid="hero-chip-prelaunch" className="animate-float backdrop-blur-xl bg-white/10 border border-white/20 px-5 py-3 text-[10px] uppercase tracking-[0.3em] text-white">
          Pre-Launch Live
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 3.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-[38%] right-[13%] hidden xl:block z-10"
      >
        <SpinBadge />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3.3, duration: 0.8 }}
        className="absolute top-[56%] right-[4%] hidden lg:block z-10"
      >
        <div data-testid="hero-chip-connectivity" className="animate-float-delayed backdrop-blur-xl bg-white/10 border border-white/20 px-5 py-3 text-[10px] uppercase tracking-[0.3em] text-white">
          EEH 1 Min · Metro 3 Min
        </div>
      </motion.div>
    </section>
  );
}
